"use client";

import React, { useState, useEffect } from "react";
import { MapPin, Mail, Phone, ArrowRight, ArrowLeft, CircleCheck, Loader, Github, Twitter, Linkedin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Toaster } from "@/components/ui/sonner";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const socialLinks = [{
  icon: Twitter,
  href: "#",
  label: "Twitter"
}, {
  icon: Linkedin,
  href: "#",
  label: "LinkedIn"
}, {
  icon: Github,
  href: "#",
  label: "GitHub"
}];
const budgetOptions = ["Elementary (K-5)", "Middle School (6-8)", "High School (9-12)", "College / Adult"];
const numStudentOptions = ["1", "2", "3", "4"];
function CollaborationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm,
    watch,
    register
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const watchedStudentCount = watch('student_count');
  const [studentCount, setStudentCount] = useState(watchedStudentCount || '1');
  useEffect(() => {
    if (watchedStudentCount) {
      setStudentCount(watchedStudentCount);
    }
  }, [watchedStudentCount]);
  const nextStep = (e: React.MouseEvent) => {
    e.preventDefault();
    setStep(2);
  };
  const prevStep = (e: React.MouseEvent) => {
    e.preventDefault();
    setStep(1);
  };
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-lg bg-primary/10 flex items-center justify-center">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-default font-semibold text-primary">Inquiry Received</h3>
          <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
        </div>
        <Button onClick={() => {
        resetForm();
        setStep(1);
      }} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary/5">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  const inputStyles = "border-0 border-b border-border rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-primary bg-transparent dark:bg-transparent transition-colors font-default";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="flex items-center gap-4 mb-8">
        <div className={`h-1 flex-1 rounded-lg transition-colors duration-300 ${step >= 1 ? 'bg-primary' : 'bg-muted'}`} />
        <div className={`h-1 flex-1 rounded-lg transition-colors duration-300 ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
      </div>

      <div className={`space-y-6 transition-all duration-500 ${step === 1 ? 'block opacity-100' : 'hidden opacity-0'}`}>
        <div className="space-y-2 mb-6">
          <h3 className="text-xl font-default font-semibold text-primary">Step 1: Student Information</h3>
          <p className="text-sm text-muted-foreground">Tell us who will be embarking on this academic journey.</p>
        </div>

        <div className="mb-8">
          <FormField control={control} name="student_count" rules={{
          required: "Please select how many students will participate"
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default font-semibold">Number of Students *</FormLabel>
                  <FormControl>
                    <select {...field} className={`w-full h-9 bg-transparent outline-none text-sm ${inputStyles} appearance-none cursor-pointer`}>
                      <option value="" disabled className="text-muted-foreground dark:bg-background">Select number of students</option>
                      {numStudentOptions.map((option, i) => <option key={i} value={option} data-index={i} className="dark:bg-background text-foreground">
                          {option}
                        </option>)}
                    </select>
                  </FormControl>
                  <FormDescription className="font-default text-xs">How many students will participate?</FormDescription>
                  <FormMessage />
                </FormItem>} />
        </div>

        <div className="space-y-10">
          {Array.from({
          length: parseInt(studentCount) || 1
        }).map((_, i) => {
          const studentIndex = i + 1;
          return <div key={i} data-index={i} className="space-y-6 p-6 rounded-lg border border-border/50 bg-muted/20">
                <h4 className="text-sm font-bold uppercase tracking-widest text-primary/70">Student {studentIndex}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <FormItem>
                    <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default font-semibold">Student Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Alex Johnson" className={inputStyles} {...register(`student_${studentIndex}_name`, {
                    required: "Please enter the student's full name"
                  })} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>

                  <FormItem>
                    <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default font-semibold">Parent/Guardian Email *</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="parent@example.com" className={inputStyles} {...register(`student_${studentIndex}_email`, {
                    required: "Please enter a valid parent or guardian email address",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Please enter a valid email address (e.g., parent@example.com)"
                    }
                  })} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <FormItem>
                    <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default font-semibold">Current School</FormLabel>
                    <FormControl>
                      <Input placeholder="Berkeley High School" className={inputStyles} {...register(`student_${studentIndex}_school`)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>

                  <FormItem>
                    <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default font-semibold">Contact Phone *</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="(510) 555-0123" className={inputStyles} {...register(`student_${studentIndex}_phone`, {
                    required: "Please provide a contact phone number",
                    pattern: {
                      value: /^[0-9+\-\s()]*$/,
                      message: "Please enter a valid phone number (e.g., (510) 555-0123)"
                    }
                  })} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>
              </div>;
        })}
        </div>

        <div className="flex justify-end pt-4">
          <Button type="button" onClick={nextStep} className="group bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg shadow-md">
            Next Step
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </div>

      <div className={`space-y-6 transition-all duration-500 ${step === 2 ? 'block opacity-100' : 'hidden opacity-0'}`}>
        <div className="space-y-2 mb-6">
          <h3 className="text-xl font-default font-semibold text-primary">Step 2: Academic Goals</h3>
          <p className="text-sm text-muted-foreground">Help us understand the specific math challenges and objectives.</p>
        </div>

        <FormField control={control} name="project_details" rules={{
        required: "Please describe the main math topic or concept where support is needed"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default font-semibold">Biggest Math Challenge *</FormLabel>
              <FormControl>
                <Textarea placeholder="Describe the specific topics or concepts where support is needed most." className={`min-h-[120px] resize-none ${inputStyles}`} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="budget" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default font-semibold">Current Grade Level</FormLabel>
                <FormControl>
                  <select {...field} className={`w-full h-9 bg-transparent outline-none text-sm ${inputStyles} appearance-none cursor-pointer`}>
                    <option value="" disabled className="text-muted-foreground dark:bg-background">Select grade...</option>
                    {budgetOptions.map((option, i) => <option key={i} value={option} data-index={i} className="dark:bg-background text-foreground">
                        {option}
                      </option>)}
                  </select>
                </FormControl>
                <FormDescription className="font-default text-xs">Select the student's current academic level.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="timeline" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default font-semibold">Target Start Date</FormLabel>
                <FormControl>
                  <Input type="date" className={inputStyles} {...field} />
                </FormControl>
                <FormDescription className="font-default text-xs">When would you like to begin tutoring?</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive font-medium">{submitError}</p>
          </div>}

        <div className="flex justify-between pt-4">
          <Button type="button" variant="ghost" onClick={prevStep} className="group text-muted-foreground hover:text-primary">
            <ArrowLeft className="mr-2 size-4 transition-transform group-hover:-translate-x-1" />
            Previous
          </Button>
          <Button type="submit" disabled={isSubmitting} className="min-w-[160px] bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-lg shadow-md font-semibold">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Processing...
              </> : 'Request Assessment'}
          </Button>
        </div>
      </div>
    </Form>;
}
export default function LetsCollaborate() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="238034" id="lets-build-confidence" className="py-24 lg:py-32 relative overflow-hidden bg-background" ref={sectionRef}>
      <Toaster position="bottom-right" />
      
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#00326208_1px,transparent_1px),linear-gradient(to_bottom,#00326208_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          <div className={`lg:col-span-5 space-y-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="space-y-6">
              <div>
                <h2 className="text-4xl md:text-6xl font-serif font-bold tracking-tight text-primary mb-4">
                  Let's Build Confidence
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed font-default">The first step toward mathematical mastery begins here. Connect with Mr. Cottone to unlock your student's full potential.</p>
              </div>
              <div className="pt-2">
                <Link to="https://calendar.google.com/calendar/appointments/schedules/AcZssZ3cbWwqgdydbKF6nDrQEiJTUQsW40ZFVmFmb3jtCzMCbEKqOXs_8uEF8mz04QQFsfkCSk1dyMqw"><Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-bold px-8 rounded-lg shadow-lg transition-all hover:scale-105">
                    Book a Session
                  </Button></Link>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-lg bg-accent text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shadow-sm">
                  <CircleCheck className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold font-default text-primary">What Happens Next</h4>
                  <p className="text-muted-foreground mt-1 text-sm">1. Personal email response within 24 hours.<br />2. 15-minute discovery call to discuss goals.<br />3. Initial assessment scheduled.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-lg bg-accent text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shadow-sm">
                  <Mail className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold font-default text-primary">Email</h4>
                  <p className="text-muted-foreground mt-1">
                    <Link to="mailto:robert@mathwithmrcottone.com" className="hover:text-secondary transition-colors text-foreground font-default">robert@mathwithmrcottone.com</Link>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-lg bg-accent text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shadow-sm">
                  <Phone className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold font-default text-primary">Call or Text</h4>
                  <p className="text-muted-foreground mt-1">
                    <Link to="tel:+15550198273" className="hover:text-secondary transition-colors text-foreground font-default">+1 (480) 430-7841</Link>
                  </p>
                </div>
              </div>
            </div>

            <Separator className="bg-border/50" />

            <div className="space-y-4">
              <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground font-mono">Academic Network</h4>
              <div className="flex gap-4">
                {socialLinks.map((social, i) => {
                const Icon = social.icon;
                return <Link key={i} to={social.href} data-index={i} className="p-2 rounded-lg bg-muted text-primary hover:bg-secondary hover:text-secondary-foreground transition-colors shadow-sm" aria-label={social.label}>
                      <Icon className="size-5" />
                    </Link>;
              })}
              </div>
            </div>

            <div className="relative aspect-[4/3] w-full rounded-lg overflow-hidden border border-border/50 bg-muted/30 group shadow-lg !bg-[url(https://buildingmathconfidence.com/wp-content/uploads/2026/06/Math-Inverted-Color.jpg)]">
              <div className="absolute inset-0 !bg-[url(https://buildingmathconfidence.com/wp-content/uploads/2026/06/Math-Inverted-Color.jpg)] bg-contain bg-no-repeat bg-center transition-all duration-700" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <div className="flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-border/50 shadow-sm !justify-center">
                  <div className="size-2 rounded-lg bg-secondary animate-pulse" />
                  <span className="text-xs font-mono font-medium text-primary">VIRTUAL_CLASSROOM_LIVE</span>
                </div>
              </div>
            </div>
          </div>

          <div className={`lg:col-span-7 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Card className="border-border/50 shadow-xl shadow-shadow-color/5 bg-card/80 backdrop-blur-xl h-full rounded-lg">
              <CardContent className="p-8 md:p-10">
                {}
                <FormProvider formKey="confidence_form_start_learning" formId="confidence_form_start_learning" sectionName="lets-build-confidence" defaultValues={{
                student_count: "1",
                student_1_name: "",
                student_1_email: "",
                student_1_school: "",
                student_1_phone: "",
                student_2_name: "",
                student_2_email: "",
                student_2_school: "",
                student_2_phone: "",
                student_3_name: "",
                student_3_email: "",
                student_3_school: "",
                student_3_phone: "",
                student_4_name: "",
                student_4_email: "",
                student_4_school: "",
                student_4_phone: "",
                project_details: "",
                budget: "",
                timeline: ""
              }} submitText="Request Assessment" successMessage="Your inquiry has been received. I will review your student's needs and reach out within 24 hours to schedule our discovery call.">
                  <CollaborationForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}